import React from 'react';
import {
  DetailsList,
  SelectionMode,
  DetailsRow,
  DetailsHeader,
  mergeStyles,
  Icon,
  DetailsListLayoutMode,
} from 'office-ui-fabric-react';
import { getFileTypeIconProps, FileIconType } from '@uifabric/file-type-icons';
import { useTranslation } from 'react-i18next';
import { decodeHtmlentities } from '../../../utilities/html';

const colorStyle = {
  root: {
    backgroundColor: '#FAFAFA',
  },
};

const FileList = ({
  onDownloadFile,
  onDeleteFile,
  items,
  isCompact,
  headerText,
}) => {
  const { t: translator } = useTranslation();

  const itemsNotEmpty = items && items.length > 0;

  const onDownloadFileLocal = (value) => {
    itemsNotEmpty && onDownloadFile(value);
  };

  return !itemsNotEmpty ? null : (
    <div className="create-hearing-form-field">
      <h2 className="h2-style">{headerText}</h2>
      <DetailsList
        items={items}
        ariaLabel={translator('ariaLabel.fileListLabel')}
        ariaLabelForSelectAllCheckbox={translator('ariaLabel.listSelectAll')}
        ariaLabelForSelectionColumn={translator(
          'ariaLabel.listSelectionColumn',
        )}
        compact={isCompact}
        selectionMode={SelectionMode.none}
        columns={columns({
          onDeleteClick: onDeleteFile,
          translator: translator,
          filesToShow: items,
          isDeleteable: itemsNotEmpty && onDeleteFile,
        })}
        layoutMode={DetailsListLayoutMode.justified}
        onRenderDetailsHeader={(props) => {
          return (
            <DetailsHeader
              {...props}
              styles={colorStyle}
              ariaLabel={translator('ariaLabel.listHeader')}
            />
          );
        }}
        onRenderRow={(props) => {
          return (
            <DetailsRow
              className="appendix-list-row"
              {...props}
              styles={colorStyle}
              tabIndex="0"
            />
          );
        }}
        onItemInvoked={onDownloadFileLocal}
      />
    </div>
  );
};

export { FileList };

const iconClass = mergeStyles({
  fontSize: '20px',
  margin: '8px',
});
function columns({ translator, filesToShow, isDeleteable, onDeleteClick }) {
  return [
    {
      className: 'icon-column',
      key: 'icon',
      name: '',
      ariaLabel: 'icon',
      fieldName: 'icon',
      isPadded: true,
      isResizable: false,
      isMultiline: false,
      minWidth: 50,
      maxWidth: 50,
      isSorted: false,
      isSortedDescending: false,
      onRender: (item) => {
        switch (
          (typeof item.fileType === 'string' ? item.fileType : '').toLowerCase()
        ) {
          case 'pdf': {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    extension: 'pdf',
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
          case 'csv': {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    extension: 'csv',
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
          case 'docx' || 'doc': {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    extension: 'docx',
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
          case 'xls' || 'xlsx' || 'xlsm' || 'xltx' || 'xltm': {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    extension: 'xlsx',
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
          case 'pptx' || 'ppt': {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    extension: 'pptx',
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
          case '': {
            return <div className="appendix-list-column"></div>;
          }
          default: {
            return (
              <div className="appendix-list-column">
                <Icon
                  {...getFileTypeIconProps({
                    type: FileIconType.file,
                    size: 32,
                    imageFileType: 'svg',
                  })}
                  className={iconClass}
                />
              </div>
            );
          }
        }
      },
    },
    {
      className: 'fileName-column',
      key: 'fileName',
      name: translator('global.name'),
      ariaLabel: translator('global.name'),
      fieldName: 'fileName',
      isPadded: true,
      isResizable: false,
      isMultiline: false,
      minWidth: 250,
      isSorted: false,
      isSortedDescending: false,
      onRender: (item) => {
        return (
          <div className="appendix-list-column">
            <p>{decodeHtmlentities(item.fileName)}</p>
          </div>
        );
      },
    },
    {
      className: 'fileType-column',
      key: 'fileType',
      name: translator('global.type'),
      ariaLabel: translator('global.type'),
      fieldName: 'fileType',
      isPadded: true,
      isResizable: false,
      isMultiline: false,
      minWidth: 50,
      maxWidth: 50,
      isSorted: false,
      isSortedDescending: false,
      onRender: (item) => {
        return (
          <div className="appendix-list-column">
            <p>{item.fileType}</p>
          </div>
        );
      },
    },
    {
      className: 'removeItem-column',
      key: 'removeItem',
      fieldName: 'removeItem',
      ariaLabel: translator('components.fileComponents.deleteFile'),
      isPadded: true,
      isResizable: false,
      isMultiline: false,
      minWidth: 50,
      maxWidth: 50,
      isSorted: false,
      isSortedDescending: false,
      onRender: (item) => {
        return isDeleteable ? (
          <div
            className="appendix-list-remove-file"
            onClick={() => {
              onDeleteClick(item);
            }}
          >
            <Icon
              iconName="Cancel"
              className={mergeStyles({ fontSize: 10, margin: '0 8px' })}
            />
            <p>{translator('components.fileComponents.deleteFile')}</p>
          </div>
        ) : (
          <div></div>
        );
      },
    },
  ];
}
